// PCControlTextHelper (PCControlTextHelper.cpp): the `%Ctrl_<Action>[BIG]%` tokens of a text
// library string replaced by the controls the principal user's binder gives the action. Callers in
// the original: UI_LocaliserFunc 0xa9f090 (every IP2 string), WormHUDPanel::DrawEnterVehicle
// 0xbfedd0 and WormHUDMan::__RenderWorldSystemText 0xb2f3a0. The browser has no pad, so
// `UserMan::WasLastInputFromAPad` is false and the source is `RCT_Keyboard`'s "Keyboard"
// (`UserMan::GetRemappableControllerSource` 0xcc9800); the binder is the client's
// (game/input.ts `keyboardActions`). Not reached in the original (no callers):
// GetCharacterForActionName 0xb79240, GetCharacterForInputChannelName 0xb798c0 and their
// GetCharacterForKeyCode 0xb79200 (SDL key names).
import { keyboardActions } from '../../game/input.ts';

/** `UserMan::GetRemappableControllerSource(RCT_Keyboard)`. */
const SOURCE = 'Keyboard';

/** The controls of `gTokenLookup` 0x1b76620 (49 TokenInfo): every control as "%Ctrl_<c>%" with
 * GlyphSize 0, then every one but Context1 as "%Ctrl_<c>BIG%" with GlyphSize 1, in this order. */
const CONTROLS = ['Start', 'Accept', 'Cancel', 'Fire', 'Jump', 'Fuse', 'Toggle', 'WeaponPanel', 'WormMove', 'WormJetpack', 'Aim', 'CameraMove',
  'CameraZoomIn', 'CameraZoomOut', 'Undo', 'CameraCentre', 'TabNext', 'TabPrevious', 'Pause', 'ToggleNames', 'AltFire', 'Enter', 'Context1',
  'Target', 'WeaponPanelAccept'];
export const TOKENS: [token: string, glyphSize: number][] = [
  ...CONTROLS.map((c): [string, number] => [`%Ctrl_${c}%`, 0]),
  ...CONTROLS.filter(c => c !== 'Context1').map((c): [string, number] => [`%Ctrl_${c}BIG%`, 1]),
];

/** scripts:/Scripts/PCControlTextHelper.txt (`GetControlHelperDatum` 0xb78c00): each control's
 * children in order, `Channel=<binder channel>` or `<source>="<text>"`; "Ctrl_<c>" and
 * "Ctrl_<c>BIG" have the same children. */
const C = (...channels: string[]): [string, string][] => channels.map(ch => ['Channel', ch]);
export const HELPER: Record<string, [name: string, data: string][]> = {
  Start: C('UserMenu'), Accept: C('Select'), Cancel: C('Cancel'), Fire: C('WormFireWeapon'), Jump: C('WormJumpPrimary'), Fuse: C('FuseNext'),
  Toggle: C('TargetDirection'), WeaponPanel: C('NewUI_WeaponPanel'), WormMove: C('WormLeft', 'WormRight'), WormJetpack: C('WormUp', 'WormLeft', 'WormRight'),
  Aim: C('WormUp', 'WormDown'), CameraMove: [['Keyboard', 'Gui.Mouse.Move'], ...C('CameraAxisX', 'CameraAxisY')],
  CameraZoomIn: [['Keyboard', 'Gui.Mouse.WheelUp'], ...C('CameraZoomInPad')], CameraZoomOut: [['Keyboard', 'Gui.Mouse.WheelDown'], ...C('CameraZoomOutPad')],
  Undo: C('Unknown'), CameraCentre: C('CameraCentre'), TabNext: C('NavigateTabRight'), TabPrevious: C('NavigateTabLeft'), Pause: C('NewUI_Pause'),
  ToggleNames: C('WormPanelNext'), AltFire: C('WormAltFireWeapon'), Enter: C('WormEnterExitVehicle'), Context1: C('NewUI_Context1'),
  Target: C('TargetLeftRight', 'TargetUpDown', 'TargetUp', 'TargetDown', 'TargetLeft', 'TargetRight'), WeaponPanelAccept: C('Select'),
};

/** `gGlyphLookup` 0x1b76280 (38 GlyphInfo): a pad control's display id and the text library
 * entries of its glyph code (small, BIG). */
const STICK = (id: string, g: string): [string, string, string][] => ['.Negative', '.Positive', ''].map(s => [`Gui.GamePad.${id}${s}`, `Control.Glyph.${g}`, `Control.Glyph.${g}.BIG`]);
const PAD = (id: string, g: string): [string, string, string] => [`Gui.GamePad.${id}`, `Control.Glyph.${g}`, `Control.Glyph.${g}.BIG`];
export const GLYPHS: [control: string, small: string, big: string][] = [
  ...STICK('LeftX', 'LeftStick'), ...STICK('LeftY', 'LeftStick'), ...STICK('RightX', 'RightStick'), ...STICK('RightY', 'RightStick'),
  ...['DpadLeft', 'DpadRight', 'DpadUp', 'DpadDown'].map(d => PAD(d, 'DPad')),
  PAD('A', 'A'), PAD('B', 'B'), PAD('X', 'X'), PAD('Y', 'Y'), PAD('Lstick', 'LeftStickButton'), PAD('Rstick', 'RightStickButton'),
  PAD('Lbutton', 'LeftBumper'), PAD('Rbutton', 'RightBumper'), PAD('Ltrigger', 'LeftTrigger'), PAD('Rtrigger', 'RightTrigger'), PAD('Start', 'Menu'),
  PAD('Back', 'View'), ...Array.from({ length: 10 }, (_, i) => PAD(`Button${i}`, `Button${i}`)),
];

/** `Gui::TextLibFind`'s table lookup (null: missing). */
export type TextLookup = (key: string) => string | null;

/** `ToNumber` 0xb789f0: strtoul, base 16 after "0x"/"0X"; false for 0xffffffff. strtoul skips
 * leading blanks, takes an optional sign and stops at the first non-digit. */
export function toNumber(text: string): number | null {
  const hex = text[0] === '0' && (text[1] === 'x' || text[1] === 'X');
  const m = (hex ? /^\s*([+-]?)(?:0[xX])?([0-9a-fA-F]*)/ : /^\s*([+-]?)([0-9]*)/).exec(text);
  let v = m && m[2] ? Number.parseInt(m[2], hex ? 16 : 10) : 0;
  if (!Number.isFinite(v) || v > 0xffffffff) v = 0xffffffff;
  if (m && m[1] === '-' && v !== 0) v = (0x100000000 - v) % 0x100000000;
  return v === 0xffffffff ? null : v;
}

/** `GetGlyphCode` 0xb79130: the glyph code of a pad control's display id (below 0x10000). */
function glyphCode(size: number, id: string, text: TextLookup): number | null {
  const g = GLYPHS.find(e => e[0] === id);
  const t = g ? text(size ? g[2] : g[1]) : null;
  const v = t === null ? null : toNumber(t);
  return v !== null && v < 0x10000 ? v : null;
}

/** `XString::Split` 0x126f740 (an empty string gives nothing; a separator right after another
 * one, or at the start, stays in the next piece). */
export function split(s: string, token: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i + 1] === token && start !== i + 1) {
      out.push(s.slice(start, i + 1));
      start = i + 2;
    }
  }
  if (start < s.length) out.push(s.slice(start));
  return out;
}

/** `UserMan::ExtractControlString` 0xcc9f20 (bLocalize false) over a channel's keyboard `Get`s:
 * a one-character action as is, else "Gui.Keyboard.<action>"; two spaces between `Get`s. */
function extract(actions: string[]): string {
  let display = '';
  for (const a of actions) {
    if (display) display += '  ';
    display += a === '' ? 'Gui.Control.NoControl' : a.length === 1 ? a : `Gui.${SOURCE}.${a}`;
  }
  return display;
}

/** `RetrieveControlsText` 0xb78d80: the display ids of a token's controls joined by spaces; the
 * token itself when the helper has no such control, and `result = token` (later channels still
 * append) for a channel the binder definition lacks. */
export function retrieve(token: string): string {
  const name = token.replace(/%/g, '').replace(/^Ctrl_/, '').replace(/BIG$/, '');
  const children = HELPER[name];
  if (!children) return token;
  let result = '';
  for (const [n, data] of children) {
    let ch = '';
    if (n === 'Channel') {
      const actions = keyboardActions(data);
      if (actions === null) {
        result = token;
        continue;
      }
      ch = extract(actions).replace(/ {2}/g, ' ');
    } else if (n === SOURCE) ch = data;
    if (ch) result = result ? `${result} ${ch}` : ch;
  }
  return result;
}

/** `ProcessControlsText` 0xb792c0: each id a pad glyph (a repeated glyph dropped) or
 * "<" + (one character as is, else TextLibFind) + ">". */
export function process(size: number, result: string, text: TextLookup): string {
  const seen: number[] = [];
  const ids = split(result, ' ').map(id => {
    const g = glyphCode(size, id, text);
    if (g === null) return `<${id.length === 1 ? id : (text(id) ?? id)}>`;
    if (seen.includes(g)) return '';
    seen.push(g);
    return String.fromCharCode(g);
  });
  return ids.filter(id => id !== '').join(' ');
}

/** `ReplaceTokensWithPCControlsInString` 0xb79720: every token of `TOKENS` found in `s`
 * (`XString::Find`) replaced everywhere (`XString::Replace`) by its controls. */
export function replaceControlTokens(s: string, text: TextLookup): string {
  for (const [token, size] of TOKENS) {
    if (s.includes(token)) s = s.split(token).join(process(size, retrieve(token), text));
  }
  return s;
}
